/**
 * Knex implementation of SchemaAdapter.
 *
 * Provides normalized DDL operations using Knex schema builder.
 * Supports SQLite, MySQL, and PostgreSQL with database-specific adaptations.
 */

import type { Knex } from 'knex'
import type {
  SchemaAdapter,
  SchemaTableBuilder,
  SchemaAlterTableBuilder,
  SchemaColumnBuilder,
  SchemaColumnInfo,
  SchemaForeignKeyBuilder
} from '@gzl10/nexus-sdk'
import { logger } from '../../core/index.js'
import { getDbClient } from './schema-helpers.js'

/**
 * Knex column builder wrapper implementing SchemaColumnBuilder.
 */
class KnexColumnBuilder implements SchemaColumnBuilder {
  constructor(private column: Knex.ColumnBuilder) {}

  primary(): SchemaColumnBuilder {
    this.column.primary()
    return this
  }

  unique(): SchemaColumnBuilder {
    this.column.unique()
    return this
  }

  index(): SchemaColumnBuilder {
    this.column.index()
    return this
  }

  nullable(): SchemaColumnBuilder {
    this.column.nullable()
    return this
  }

  notNullable(): SchemaColumnBuilder {
    this.column.notNullable()
    return this
  }

  defaultTo(value: unknown): SchemaColumnBuilder {
    this.column.defaultTo(value as Knex.Value)
    return this
  }

  references(column: string): SchemaColumnBuilder {
    this.column.references(column)
    return this
  }

  inTable(table: string): SchemaColumnBuilder {
    ;(this.column as Knex.ReferencingColumnBuilder).inTable(table)
    return this
  }

  onDelete(action: 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION'): SchemaColumnBuilder {
    ;(this.column as Knex.ReferencingColumnBuilder).onDelete(action)
    return this
  }
}

/**
 * Knex foreign key builder wrapper implementing SchemaForeignKeyBuilder.
 * Note: Knex foreign() returns ForeignConstraintBuilder, references() returns ReferencingColumnBuilder
 */
class KnexForeignKeyBuilder implements SchemaForeignKeyBuilder {
  private refBuilder: Knex.ReferencingColumnBuilder | null = null

  constructor(private fk: Knex.ForeignConstraintBuilder) {}

  references(column: string): SchemaForeignKeyBuilder {
    this.refBuilder = this.fk.references(column)
    return this
  }

  inTable(table: string): SchemaForeignKeyBuilder {
    if (this.refBuilder) {
      this.refBuilder.inTable(table)
    }
    return this
  }

  onDelete(action: 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION'): SchemaForeignKeyBuilder {
    if (this.refBuilder) {
      this.refBuilder.onDelete(action)
    }
    return this
  }

  onUpdate(action: 'CASCADE' | 'SET NULL' | 'RESTRICT' | 'NO ACTION'): SchemaForeignKeyBuilder {
    if (this.refBuilder) {
      this.refBuilder.onUpdate(action)
    }
    return this
  }
}

/**
 * Knex table builder wrapper implementing SchemaTableBuilder.
 */
class KnexTableBuilder implements SchemaTableBuilder {
  constructor(
    protected table: Knex.CreateTableBuilder | Knex.AlterTableBuilder,
    protected knex: Knex
  ) {}

  string(name: string, size?: number): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.string(name, size))
  }

  text(name: string): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.text(name))
  }

  integer(name: string): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.integer(name))
  }

  decimal(name: string, precision?: number, scale?: number): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.decimal(name, precision, scale))
  }

  boolean(name: string): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.boolean(name))
  }

  date(name: string): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.date(name))
  }

  datetime(name: string): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.datetime(name))
  }

  json(name: string): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.json(name))
  }

  uuid(name: string): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.uuid(name))
  }

  bigInteger(name: string): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.bigInteger(name))
  }

  timestamp(name: string): SchemaColumnBuilder {
    return new KnexColumnBuilder((this.table as Knex.CreateTableBuilder).timestamp(name))
  }

  jsonb(name: string): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.jsonb(name))
  }

  enum(name: string, values: readonly string[]): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.enum(name, values as string[]))
  }

  binary(name: string, length?: number): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.binary(name, length))
  }

  float(name: string, precision?: number, scale?: number): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.float(name, precision, scale))
  }

  specificType(name: string, type: string): SchemaColumnBuilder {
    return new KnexColumnBuilder(this.table.specificType(name, type))
  }

  index(columns: string | string[], indexName?: string): void {
    ;(this.table as Knex.CreateTableBuilder).index(columns, indexName)
  }

  unique(columns: string | string[], constraintName?: string): void {
    ;(this.table as Knex.CreateTableBuilder).unique(columns, { indexName: constraintName })
  }

  primary(columns: string | string[], constraintName?: string): void {
    const cols = Array.isArray(columns) ? columns : [columns]
    ;(this.table as Knex.CreateTableBuilder).primary(cols, { constraintName })
  }

  foreign(column: string): SchemaForeignKeyBuilder {
    return new KnexForeignKeyBuilder((this.table as Knex.CreateTableBuilder).foreign(column))
  }

  increments(name?: string): SchemaColumnBuilder {
    return new KnexColumnBuilder((this.table as Knex.CreateTableBuilder).increments(name))
  }

  bigIncrements(name?: string): SchemaColumnBuilder {
    return new KnexColumnBuilder((this.table as Knex.CreateTableBuilder).bigIncrements(name))
  }

  timestamps(): void {
    const client = getDbClient(this.knex)

    if (client === 'postgres') {
      ;(this.table as Knex.CreateTableBuilder).timestamp('created_at', { useTz: true }).defaultTo(this.knex.fn.now())
      ;(this.table as Knex.CreateTableBuilder).timestamp('updated_at', { useTz: true }).defaultTo(this.knex.fn.now())
    } else if (client === 'sqlite') {
      this.table.text('created_at').defaultTo(this.knex.raw("(datetime('now'))"))
      this.table.text('updated_at').defaultTo(this.knex.raw("(datetime('now'))"))
    } else {
      // MySQL
      ;(this.table as Knex.CreateTableBuilder).timestamp('created_at').defaultTo(this.knex.fn.now())
      ;(this.table as Knex.CreateTableBuilder).timestamp('updated_at').defaultTo(this.knex.fn.now())
    }
  }
}

/**
 * Knex alter table builder wrapper implementing SchemaAlterTableBuilder.
 */
class KnexAlterTableBuilder extends KnexTableBuilder implements SchemaAlterTableBuilder {
  constructor(table: Knex.AlterTableBuilder, knex: Knex) {
    super(table, knex)
  }

  dropColumn(name: string): void {
    ;(this.table as Knex.AlterTableBuilder).dropColumn(name)
  }

  renameColumn(from: string, to: string): void {
    ;(this.table as Knex.AlterTableBuilder).renameColumn(from, to)
  }
}

/**
 * Knex-based implementation of SchemaAdapter.
 *
 * @example
 * const schema = new KnexSchemaAdapter(knexInstance)
 *
 * if (!await schema.hasTable('users')) {
 *   await schema.createTable('users', (t) => {
 *     t.string('id').primary()
 *     t.string('email').unique().notNullable()
 *     t.timestamps()
 *   })
 * }
 */
export class KnexSchemaAdapter implements SchemaAdapter {
  constructor(private knex: Knex) {}

  /**
   * Raw access to Knex schema builder.
   */
  get raw(): unknown {
    return this.knex.schema
  }

  async hasTable(tableName: string): Promise<boolean> {
    return this.knex.schema.hasTable(tableName)
  }

  async hasColumn(tableName: string, columnName: string): Promise<boolean> {
    return this.knex.schema.hasColumn(tableName, columnName)
  }

  async getColumns(tableName: string): Promise<SchemaColumnInfo[]> {
    const columnInfo = await this.knex(tableName).columnInfo()
    return Object.entries(columnInfo).map(([name, info]) => ({
      name,
      type: (info as { type: string }).type,
      nullable: (info as { nullable: boolean }).nullable,
      defaultValue: (info as { defaultValue: unknown }).defaultValue
    }))
  }

  async createTable(tableName: string, builder: (t: SchemaTableBuilder) => void): Promise<void> {
    await this.knex.schema.createTable(tableName, (table) => {
      builder(new KnexTableBuilder(table, this.knex))
    })
    logger.info(`Created table: ${tableName}`)
  }

  async alterTable(tableName: string, builder: (t: SchemaAlterTableBuilder) => void): Promise<void> {
    await this.knex.schema.alterTable(tableName, (table) => {
      builder(new KnexAlterTableBuilder(table, this.knex))
    })
    logger.info(`Altered table: ${tableName}`)
  }

  async dropTable(tableName: string): Promise<void> {
    await this.knex.schema.dropTableIfExists(tableName)
    logger.info(`Dropped table: ${tableName}`)
  }

  async addTimestamps(tableName: string): Promise<void> {
    const hasCreatedAt = await this.hasColumn(tableName, 'created_at')
    if (hasCreatedAt) return

    await this.knex.schema.alterTable(tableName, (table) => {
      const client = getDbClient(this.knex)

      if (client === 'postgres') {
        table.timestamp('created_at', { useTz: true }).defaultTo(this.knex.fn.now())
        table.timestamp('updated_at', { useTz: true }).defaultTo(this.knex.fn.now())
      } else if (client === 'sqlite') {
        table.text('created_at').defaultTo(this.knex.raw("(datetime('now'))"))
        table.text('updated_at').defaultTo(this.knex.raw("(datetime('now'))"))
      } else {
        table.timestamp('created_at').defaultTo(this.knex.fn.now())
        table.timestamp('updated_at').defaultTo(this.knex.fn.now())
      }
    })
    logger.info(`Added timestamps to: ${tableName}`)
  }

  async addAuditFields(tableName: string): Promise<void> {
    const hasCreatedBy = await this.hasColumn(tableName, 'created_by')
    if (hasCreatedBy) return

    await this.knex.schema.alterTable(tableName, (table) => {
      table.string('created_by').nullable()
      table.string('updated_by').nullable()
    })
    logger.info(`Added audit fields to: ${tableName}`)
  }

  async addColumnIfMissing(tableName: string, columnName: string, type: string): Promise<boolean> {
    const exists = await this.hasColumn(tableName, columnName)
    if (exists) return false

    await this.knex.schema.alterTable(tableName, (table) => {
      switch (type) {
        case 'string':
          table.string(columnName).nullable()
          break
        case 'text':
          table.text(columnName).nullable()
          break
        case 'integer':
          table.integer(columnName).nullable()
          break
        case 'boolean':
          table.boolean(columnName).nullable()
          break
        case 'date':
          table.date(columnName).nullable()
          break
        case 'datetime':
          table.datetime(columnName).nullable()
          break
        case 'json':
          table.json(columnName).nullable()
          break
        case 'uuid':
          table.uuid(columnName).nullable()
          break
        default:
          table.string(columnName).nullable()
      }
    })
    logger.info(`Added column: ${tableName}.${columnName}`)
    return true
  }
}

/**
 * Create a KnexSchemaAdapter instance.
 *
 * @param knex - Knex instance
 * @returns SchemaAdapter implementation
 */
export function createKnexSchemaAdapter(knex: Knex): SchemaAdapter {
  return new KnexSchemaAdapter(knex)
}
